import { addDays } from 'date-fns';
import { gql } from 'graphql-tag';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import { JobStatus } from '~/gql/graphql';
import { useAuth } from '~/hooks/useAuth';
import type { FilteredPlannerViewData } from '~/routes/calendar._index';
import { PlannerView } from './PlannerView';
import { RescheduleJobDialog } from './RescheduleJobDialog';
import { RescheduleDetails } from './types';

type OptimisticMove = {
  jobId: string;
  originalSiteId: string;
  newSiteId: string;
  offsetDays: number;
  timestamp: number;
};

const RescheduleJob_Mutation = gql`
  mutation RescheduleJob($input: RescheduleJobInput!) {
    rescheduleJob(input: $input) {
      id
      status
      scheduleStart
      scheduleEnd
      scheduledStartStrict
      scheduledStartDate
      scheduledStartTime
      scheduledEndStrict
      scheduledEndDate
      scheduledEndTime
      completedAt
      permissions
    }
  }
`;

function Wrapped(props: ComponentProps<typeof PlannerView>) {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [rescheduleDetails, setRescheduleDetails] =
    useState<RescheduleDetails>();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [optimisticMoves, setOptimisticMoves] = useState<OptimisticMove[]>([]);

  const [, rescheduleJob] = useMutation(RescheduleJob_Mutation);
  const { isCurrentUser } = useAuth();

  // Apply optimistic updates to the data - memoized to prevent unnecessary re-renders
  const applyOptimisticUpdates = useMemo(() => {
    if (optimisticMoves.length === 0) return props.data;

    return props.data.map((site: FilteredPlannerViewData[number]) => {
      // Remove jobs that were moved away from this site
      const remainingJobs = site.calendar.filter(
        (job: FilteredPlannerViewData[number]['calendar'][number]) =>
          !optimisticMoves.some(
            (move) => move.jobId === job.id && move.originalSiteId === site.id
          )
      );

      // Add jobs that were moved to this site
      const incomingJobs = optimisticMoves
        .filter((move) => move.newSiteId === site.id)
        .map((move) => {
          // Find the original job from its original site
          const originalSite = props.data.find(
            (s: FilteredPlannerViewData[number]) => s.id === move.originalSiteId
          );
          const originalJob = originalSite?.calendar.find(
            (j: FilteredPlannerViewData[number]['calendar'][number]) =>
              j.id === move.jobId
          );

          if (!originalJob) return null;

          // Calculate new dates based on the original job's actual dates
          const originalStartDate = new Date(originalJob.start);
          const originalEndDate = originalJob.end
            ? new Date(originalJob.end)
            : null;

          const newStart = addDays(
            originalStartDate,
            move.offsetDays
          ).toISOString();
          const newEnd = originalEndDate
            ? addDays(originalEndDate, move.offsetDays).toISOString()
            : undefined;

          return {
            ...originalJob,
            start: newStart,
            end: newEnd,
          };
        })
        .filter((job): job is NonNullable<typeof job> => job !== null);

      return {
        ...site,
        calendar: [...remainingJobs, ...incomingJobs],
      };
    });
  }, [props.data, optimisticMoves]);

  // Add an optimistic move
  const addOptimisticMove = (move: Omit<OptimisticMove, 'timestamp'>) => {
    setOptimisticMoves((prev) => [
      ...prev.filter((m) => m.jobId !== move.jobId), // Remove any existing move for this job
      { ...move, timestamp: Date.now() },
    ]);
  };

  // Remove an optimistic move (on success or failure)
  const removeOptimisticMove = (jobId: string) => {
    setOptimisticMoves((prev) => prev.filter((m) => m.jobId !== jobId));
  };

  // Clear old optimistic moves (cleanup after 30 seconds)
  const clearOldOptimisticMoves = () => {
    const thirtySecondsAgo = Date.now() - 30000;
    setOptimisticMoves((prev) =>
      prev.filter((m) => m.timestamp > thirtySecondsAgo)
    );
  };

  // Cleanup optimistic moves on unmount to avoid stale state
  useEffect(() => {
    return () => {
      setOptimisticMoves([]);
    };
  }, []);

  // Handle immediate reschedule without dialog (Created and Accepted jobs)
  const handleImmediateReschedule = (details: RescheduleDetails) => {
    setIsRescheduling(true);

    // Optimistic update already applied in onReschedule, no need to duplicate

    // Add a timeout fallback to ensure we don't get stuck
    const timeoutId = setTimeout(() => {
      console.warn(
        'Immediate reschedule timeout - reverting optimistic update'
      );
      removeOptimisticMove(details.jobId);
      setIsRescheduling(false);
      toast.warn('Request timed out - please try again');
    }, 10000); // 10 second timeout

    const immediateInput = {
      input: {
        id: details.jobId,
        offsetDays: details.offsetDays,
        // Explicitly set optional fields to undefined to ensure clean input
        comment: undefined,
        // Note: notify field intentionally omitted for immediate reschedules (Created/Accepted-by-self)
        // as these operations should not trigger notifications
      },
    };

    rescheduleJob(immediateInput)
      .then((result) => {
        clearTimeout(timeoutId);

        if (result.error) {
          console.error('Reschedule error details:', {
            message: result.error.message,
            graphQLErrors: result.error.graphQLErrors,
            networkError: result.error.networkError,
          });
          // Revert optimistic update on error
          removeOptimisticMove(details.jobId);
          let errorMessage = 'Failed to reschedule job';
          if (result.error.message?.includes('s3')) {
            errorMessage =
              'Server configuration error - please try again later or contact support';
          } else {
            errorMessage += ': ' + result.error.message;
          }
          toast.warn(errorMessage);
        } else if (result.data) {
          // Remove optimistic update on success (real data will come from cache update)
          removeOptimisticMove(details.jobId);
        } else {
          console.warn('Reschedule completed but no data or error returned');
          // Still remove optimistic update
          removeOptimisticMove(details.jobId);
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        console.error('Reschedule catch error:', error);
        // Revert optimistic update on error
        removeOptimisticMove(details.jobId);
        let errorMessage = 'Failed to reschedule job';
        if (error.message?.includes('s3')) {
          errorMessage =
            'Server configuration error - please try again later or contact support';
        } else {
          errorMessage += ': ' + error.message;
        }
        toast.warn(errorMessage);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsRescheduling(false);
        clearOldOptimisticMoves();
      });
  };

  return (
    <>
      <RescheduleJobDialog
        isOpen={dialogIsOpen}
        onClose={() => {
          // Revert optimistic update when user clicks "Go back"
          if (rescheduleDetails) {
            removeOptimisticMove(rescheduleDetails.jobId);
          }
          setDialogIsOpen(false);
          setRescheduleDetails(undefined);
          setIsRescheduling(false);
        }}
        isLoading={isRescheduling}
        onSubmit={(_, { comment }) => {
          if (!rescheduleDetails) return;
          setIsRescheduling(true);

          // Optimistic update already applied in onReschedule, no need to duplicate

          // Add a timeout fallback to ensure we don't get stuck
          const timeoutId = setTimeout(() => {
            console.warn('Mutation timeout - reverting optimistic update');
            removeOptimisticMove(rescheduleDetails.jobId);
            setIsRescheduling(false);
            toast.warn('Request timed out - please try again');
          }, 10000); // 10 second timeout

          const mutationInput = {
            input: {
              id: rescheduleDetails.jobId,
              offsetDays: rescheduleDetails.offsetDays,
              comment: comment || undefined, // Ensure empty strings become undefined
              // Note: notify field intentionally omitted; dialog-based reschedules rely on backend
              // default behavior for notifications based on job status and assignee
            },
          };

          rescheduleJob(mutationInput)
            .then((result) => {
              clearTimeout(timeoutId);

              if (result.error) {
                console.error('Dialog reschedule error details:', {
                  message: result.error.message,
                  graphQLErrors: result.error.graphQLErrors,
                  networkError: result.error.networkError,
                });
                // Revert optimistic update on error
                removeOptimisticMove(rescheduleDetails.jobId);
                let errorMessage = 'Failed to reschedule job';
                if (result.error.message?.includes('s3')) {
                  errorMessage =
                    'Server configuration error - please try again later or contact support';
                } else {
                  errorMessage += ': ' + result.error.message;
                }
                toast.warn(errorMessage);
              } else {
                // Remove optimistic update on success (real data will come from cache update)
                removeOptimisticMove(rescheduleDetails.jobId);
                // Close dialog on success
                setDialogIsOpen(false);
                setRescheduleDetails(undefined);
              }
            })
            .catch((error) => {
              clearTimeout(timeoutId);
              console.error('Dialog reschedule catch error:', error);
              // Revert optimistic update on error
              removeOptimisticMove(rescheduleDetails.jobId);
              let errorMessage = 'Failed to reschedule job';
              if (error.message?.includes('s3')) {
                errorMessage =
                  'Server configuration error - please try again later or contact support';
              } else {
                errorMessage += ': ' + error.message;
              }
              toast.warn(errorMessage);
            })
            .finally(() => {
              clearTimeout(timeoutId);
              // Always reset loading state
              setIsRescheduling(false);
              // Only close dialog on success - let user see error and decide
              clearOldOptimisticMoves();
            });
        }}
        jobName={rescheduleDetails?.jobName ?? ''}
        assigneeName={rescheduleDetails?.assigneeName ?? ''}
        action={
          rescheduleDetails?.jobStatus === JobStatus.Offered
            ? 'offer'
            : 'confirm'
        }
        originalDate={rescheduleDetails?.originalDate}
        targetDate={rescheduleDetails?.targetDate}
      />

      <PlannerView
        {...props}
        data={applyOptimisticUpdates}
        onReschedule={(details: RescheduleDetails) => {
          // Apply optimistic update immediately for all job types
          addOptimisticMove({
            jobId: details.jobId,
            originalSiteId: details.originalSiteId || '',
            newSiteId: details.targetSiteId || '',
            offsetDays: details.offsetDays,
          });

          // Check if this requires a confirmation dialog
          if (details.jobStatus === JobStatus.Offered) {
            setRescheduleDetails(details);
            setDialogIsOpen(true);
          } else if (details.jobStatus === JobStatus.Accepted) {
            // Accepted jobs: check if current user is the assignee
            if (details.assigneeId && isCurrentUser(details.assigneeId)) {
              // Current user is assignee: immediate reschedule without dialog
              handleImmediateReschedule(details);
            } else {
              // External assignee: show dialog to confirm notifying them
              setRescheduleDetails(details);
              setDialogIsOpen(true);
            }
          } else {
            // Created jobs: immediate reschedule without dialog
            handleImmediateReschedule(details);
          }
        }}
      />
    </>
  );
}

export { Wrapped as PlannerView };
